import * as React from "react";
import classnames from "classnames";

import {
  encodeMove,
  coordinatesEqual,
  coordinatesFromUci,
  default as GameState,
  Coordinate,
  Color,
} from "./GameState";
import SpecialActions from "./SpecialActions";
import { DelayProps } from "../common";
import { Segment } from "../semantic-ui";
import styles from "./GameInterface.module.css";
import Board from "./Board";

type Props = {
  game: GameState;
  nextAction?: "move" | "offer_duel" | "resolve_duel";
  pendingDuelBid: number | undefined;
  // If specified, actions and moves for the other color will not be shown.
  viewerColor?: Color | "both";
  // Should the board be rendered with black pieces on the bottom?
  flipped?: boolean;
  // Should black's special actions be rendered upside down?
  flipBlackActions?: boolean;
  onMove: (uci: string) => void;
  onStartDuel: (duelNumber: number, bid: number) => void;
  onSkipDuel: (duelNumber: number) => void;
  onResolveDuel: (bid: number, gain: boolean) => void;
  className?: string;
};

type State = {
  // When making a move that requires a promotion, the partial UCI code is here.
  pendingPromotion?: string;
};

class GameInterface extends React.Component<Props, State> {
  board?: Board;

  state = {} as State;

  render() {
    const {
      game,
      nextAction,
      pendingDuelBid,
      viewerColor,
      flipped,
      flipBlackActions,
      onMove,
      onStartDuel,
      onSkipDuel,
      onResolveDuel,
      className,
      ...rest
    } = this.props;
    return (
      <Segment.Group
        className={classnames(styles.GameInterface, className, {
          [styles.flipped]: flipped,
        })}
        {...rest}
      >
        <div>
          {this.renderSpecialActions("black")}
          <DelayProps delay={750} flipped={flipped}>
            {(delayed_props) => (
              <Board
                ref={(b: Board) => (this.board = b)}
                style={{
                  transform: delayed_props.flipped ? "rotate(180deg)" : "none",
                }}
                stones={game.stones}
                pieces={game.pieces}
                squareClassName={this.squareClassName}
                selectableSquares={
                  game.winner === null && this.showActionsFor(game.toMove)
                    ? game.listOccupiedSquares(game.toMove)
                    : []
                }
                listReachableFrom={(c) => game.listReachableFrom(c)}
                onMove={this.handleMove}
                onSelectedSquareChange={() => this.forceUpdate()}
                {...delayed_props}
              />
            )}
          </DelayProps>
          {this.renderSpecialActions("white")}
        </div>
      </Segment.Group>
    );
  }

  squareClassName = (c: Coordinate) => {
    const { game } = this.props;
    const lastMoveCoords = this.state.pendingPromotion
      ? coordinatesFromUci(this.state.pendingPromotion)
      : game.lastMoveCoords;
    if (!lastMoveCoords) return null;
    return coordinatesEqual(lastMoveCoords[0], c)
      ? Board.squareClasses.highlightLight
      : coordinatesEqual(lastMoveCoords[1], c)
      ? Board.squareClasses.highlightDark
      : null;
  };

  showActionsFor(color: Color) {
    return (
      this.props.viewerColor === "both" || this.props.viewerColor === color
    );
  }

  handleMove = (from: Coordinate, to: Coordinate) => {
    if (this.props.game.isPromotingMove(from, to)) {
      this.setState({ pendingPromotion: encodeMove(from, to) });
    } else {
      this.handleMoveUci(encodeMove(from, to));
    }
  };

  handleMoveUci = (uci: string) => {
    this.setState({ pendingPromotion: undefined });
    this.props.onMove(uci);
  };

  renderSpecialActions(color: Color) {
    if (!this.showActionsFor(color)) return null;
    return (
      <Segment
        className={classnames(styles.specialActions, {
          [styles.flipped]: this.props.flipBlackActions && color === "black",
        })}
        basic
      >
        <SpecialActions
          {...this.props}
          color={color}
          pendingPromotion={this.state.pendingPromotion}
          selectedSquare={this.board && this.board.getSelectedSquare()}
          onMove={this.handleMoveUci}
        />
      </Segment>
    );
  }
}

export default GameInterface;
